import { useContext, useState } from 'react';
import AuthContext from '../../../context/AuthContext';
import LanguageContext from '../../../context/LanguageContext';
import FormulaireConnexion, { EcranRedirection, estInscriptionDesactivee } from '../FormulaireConnexion';
import MouseGlow from '../../ui/MouseGlow';
import heroIllustration from '../../../assets/hero-illustration-pictos.png';

/**
 * Le hero de la page d'accueil : le texte à gauche, le formulaire de
 * connexion dessous, l'illustration à droite. LandingPage, classe pour classe.
 *
 * ========================================================================
 * CE QUI SE RÉDIGE, CE QUI NE SE RÉDIGE PAS
 * ========================================================================
 *
 * Les textes d'accueil (titre, slogan, description) changent avec les
 * campagnes : ils sont des champs du bloc, traduits avec la page. Le
 * formulaire, lui, est la porte d'entrée de l'application — sa validation,
 * son appel à l'authentification, ses libellés (t.auth.*) restent dans le
 * code, partagés avec LandingPage par FormulaireConnexion.
 *
 * ========================================================================
 * DEUX CHOIX, PAS DE SAISIE LIBRE
 * ========================================================================
 *
 * - illustration : « pictos » (l'image embarquée de LandingPage, par
 *   défaut) ou « aucune », qui retire la colonne de droite.
 * - effetSouris : « oui » rend le halo qui suit le curseur de LandingPage.
 *   Non par défaut : il MASQUE LE CURSEUR sur toute la page, ce qui ne se
 *   décide pas à la légère.
 *
 * Le serveur enregistre ces valeurs en minuscules : elles sont lues sans
 * tenir compte de la casse, comme ailleurs dans les blocs.
 *
 * ========================================================================
 * L'APERÇU DE L'ADMINISTRATION
 * ========================================================================
 *
 * L'admin rend ce bloc sans les contextes de la vitrine. Leur absence le
 * signale : le formulaire y est inerte (useAuth y lèverait et emporterait
 * l'écran), et l'effet de souris n'est pas monté — il masquerait le curseur
 * dans toute l'administration.
 */

const choix = (valeur) => (typeof valeur === 'string' ? valeur.trim().toLowerCase() : '');

const AccueilConnexionBloc = ({ bloc }) => {
    const authentification = useContext(AuthContext);
    const langue = useContext(LanguageContext);
    const apercu = !authentification || !langue;

    // LandingPage remplace toute la page par l'écran de redirection. Un bloc
    // ne remplace pas la page qui le contient : il la couvre.
    const [redirection, setRedirection] = useState(false);

    const avecIllustration = choix(bloc.illustration) !== 'aucune';
    const avecEffetSouris = choix(bloc.effetSouris) === 'oui' && !apercu;

    if (redirection) {
        return (
            <div className="fixed inset-0 z-[100]">
                <EcranRedirection />
            </div>
        );
    }

    // Sans illustration, une seule colonne : la moitié droite resterait vide.
    const grille = avecIllustration
        ? 'grid grid-cols-1 md:grid-cols-2 gap-12 items-center'
        : 'grid grid-cols-1 gap-12 items-center';

    return (
        <>
            {avecEffetSouris && <MouseGlow />}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <div className={grille}>
                    <div className="space-y-6 animate-fade-in-up">
                        {(bloc.titreAccent || bloc.titre) && (
                            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
                                {bloc.titreAccent && (
                                    <span className="block text-cabaza-purple">{bloc.titreAccent}</span>
                                )}
                                {bloc.titre && (
                                    <span className="block">{bloc.titre}</span>
                                )}
                            </h1>
                        )}

                        {bloc.slogan && (
                            <p className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent">
                                {bloc.slogan}
                            </p>
                        )}

                        {bloc.description && (
                            <p className="text-base text-gray-600">{bloc.description}</p>
                        )}

                        {/* Comme le sous-titre de LandingPage : il parle de
                            ce que l'on fait une fois inscrit. */}
                        {bloc.complement && !estInscriptionDesactivee() && (
                            <p className="text-lg text-gray-600">{bloc.complement}</p>
                        )}

                        <FormulaireConnexion apercu={apercu} onRedirection={() => setRedirection(true)} />
                    </div>

                    {avecIllustration && (
                        <div className="relative animate-fade-in hidden md:block">
                            <div className="absolute inset-0 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full filter blur-3xl opacity-30 transform scale-110"></div>
                            <img
                                src={heroIllustration}
                                alt="Professionals connecting"
                                className="relative z-10 w-full h-auto hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AccueilConnexionBloc;
